'use client'

import * as React from 'react'
import { Button as ButtonPrimitive } from '@base-ui/react/button'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../lib/utils'

const buttonVariants = cva(
  // Outer button: `items-center` keeps the content group vertically
  // centred in the button's fixed height, and the inner content span
  // also uses `items-center` so icons and text centre against each
  // other. Icons (slot OR raw children) therefore align by default —
  // no per-icon translate nudge needed.
  "group/button inline-flex shrink-0 items-center justify-center border border-transparent bg-clip-padding text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Default = the venue's brand `--primary`. `--primary-foreground`
        // auto-contrasts (black on light brand colours, white on dark)
        // via `BrandKitStyles`, so this is safe across all venues.
        default:
          'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85',
        // Brand-gradient pill primary. The visual (gradient bg, pill shape,
        // typography, hover lift, shimmer pseudo) lives entirely in CSS at
        // `[data-slot="button"][data-variant="primary"]` so the look stays
        // consistent regardless of cva size/base classes. The variant only
        // adds positioning needed for the ::before pseudo to anchor.
        primary: 'relative overflow-hidden',
        // Gradient-outline pill — transparent fill, brand-gradient border via
        // a masked ::before pseudo. Visual lives in CSS at the same path.
        'secondary-pill': 'relative',
        // Transparent pill with a centre-expanding brand-gradient underline
        // on hover. Visual lives in CSS at the same path.
        'ghost-pill': 'relative',
        // Hover/active treatment kept in sync with the form-field
        // language (Input / NativeSelect / picker triggers): subtle
        // foreground-tint bg on hover, border darkens from black/10 →
        // black/20 (white/10 → white/20 in dark mode). Active /
        // aria-pressed bumps the same tokens one step further so the
        // pressed state reads as "still focused" without flipping to a
        // different colour family.
        outline:
          'border-black/10 dark:border-white/10 bg-transparent hover:bg-[var(--foreground)]/5 hover:border-black/20 dark:hover:border-white/20 active:bg-[var(--foreground)]/10 aria-expanded:bg-[var(--foreground)]/5 aria-expanded:border-black/20 dark:aria-expanded:border-white/20 aria-pressed:bg-[var(--foreground)]/10 aria-pressed:border-black/20 dark:aria-pressed:border-white/20',
        secondary:
          'border-[#2A3625] text-[#2A3625] bg-transparent hover:bg-[#2A3625]/5 active:bg-[#2A3625]/10 aria-expanded:bg-[#2A3625]/5',
        ghost:
          'bg-transparent hover:bg-[var(--foreground)]/5 active:bg-[var(--foreground)]/10 aria-expanded:bg-[var(--foreground)]/5 aria-pressed:bg-[var(--foreground)]/10',
        // Same /5 → /10 progression as ghost/outline, but rooted in the
        // destructive token instead of foreground. Pressed/expanded land
        // one step further so a confirm-press feels distinct without
        // shouting.
        destructive:
          'bg-destructive/10 text-destructive hover:bg-destructive/15 active:bg-destructive/20 aria-expanded:bg-destructive/15 aria-pressed:bg-destructive/20 focus-visible:border-destructive/40 focus-visible:ring-destructive/20 dark:bg-destructive/15 dark:hover:bg-destructive/20 dark:active:bg-destructive/25 dark:focus-visible:ring-destructive/40',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default:
          'h-8 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 px-2 text-xs has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 px-2.5 text-[0.8rem] has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        // `lg` = h-10 to match the standard Input/InputGroup height, so an
        // input + button sitting on the same row (toolbars, form submit rows)
        // align to one baseline. `default` (h-8) stays compact for dense /
        // standalone actions.
        lg: 'h-10 px-3 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        icon: 'size-8',
        'icon-xs': "size-6 [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-7',
        'icon-lg': 'size-9',
      },
      // `pill` is the brand-default — matches the day-planner header
      // / hero CTAs / chip-style triggers across the app. `square`
      // matches the form-field language (Input / Select use
      // `rounded-lg`) so a Button used as a Popover trigger inside a
      // form lines up with its neighbours instead of standing out as
      // a pill. Pick `square` whenever the button sits in a form row;
      // pick `pill` (default) everywhere else.
      shape: {
        pill: 'rounded-full',
        square: 'rounded-xl',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      shape: 'pill',
    },
  }
)

/**
 * Optional `leftIcon` / `rightIcon` slot props. The Button cva
 * already uses `has-data-[icon=inline-start]:pl-…` /
 * `has-data-[icon=inline-end]:pr-…` selectors to tighten padding on
 * the icon side; passing the icon through these props auto-stamps
 * `data-icon="inline-start"` / `"inline-end"` so callers don't have
 * to remember the convention.
 *
 * Equivalent to `<Button><LockIcon data-icon="inline-start" />Text</Button>`,
 * but reads cleaner at the callsite and guarantees the data hook is
 * applied so padding stays consistent.
 */
type ButtonProps = ButtonPrimitive.Props &
  VariantProps<typeof buttonVariants> & {
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
  }

/**
 * Inner-span gap per size. Lives next to the cva so the two stay
 * in sync — the outer cva owns the button's box (height, padding,
 * surface) and this map owns the icon-text gap inside.
 */
const INNER_GAP: Record<
  NonNullable<VariantProps<typeof buttonVariants>['size']>,
  string
> = {
  default: 'gap-1.5',
  xs: 'gap-1',
  sm: 'gap-1',
  lg: 'gap-1.5',
  icon: 'gap-0',
  'icon-xs': 'gap-0',
  'icon-sm': 'gap-0',
  'icon-lg': 'gap-0',
}

function Button({
  className,
  variant = 'default',
  size = 'default',
  shape,
  leftIcon,
  rightIcon,
  children,
  ...props
}: ButtonProps) {
  // The gradient `primary` CTA now defaults to `square` (rounded-xl); every
  // other variant keeps the `pill` default. An explicit `shape` prop wins.
  const resolvedShape = shape ?? (variant === 'primary' ? 'square' : 'pill')
  return (
    <ButtonPrimitive
      data-slot="button"
      data-variant={variant}
      className={cn(
        buttonVariants({ variant, size, shape: resolvedShape, className })
      )}
      {...props}
    >
      {/* Inner content wrapper. `items-center` so icons and text are
          vertically centred against each other — the conventional,
          predictable default. (Previously this used `items-baseline`
          + a per-icon translate nudge, which left tall icons sitting
          slightly high; centring is what callers actually expect.) */}
      <span
        data-slot="button-content"
        className={cn('inline-flex items-center', INNER_GAP[size ?? 'default'])}
      >
        {leftIcon ? markIconSlot(leftIcon, 'inline-start') : null}
        {children}
        {rightIcon ? markIconSlot(rightIcon, 'inline-end') : null}
      </span>
    </ButtonPrimitive>
  )
}

/**
 * Clone the supplied icon node with `data-icon="..."` so the
 * Button's cva selectors light up. Falls through gracefully if the
 * node isn't a valid React element (e.g. a string) — wraps it in a
 * span carrying the data attribute instead.
 */
function markIconSlot(
  node: React.ReactNode,
  slot: 'inline-start' | 'inline-end'
): React.ReactNode {
  if (React.isValidElement(node)) {
    return React.cloneElement(node as React.ReactElement<any>, {
      'data-icon': slot,
    })
  }
  return (
    <span data-icon={slot} className="inline-flex items-center">
      {node}
    </span>
  )
}

export { Button, buttonVariants }
